import React, { Component, useEffect } from "react";
import { useQuery } from "react-query";
import i18next from "i18next";
import { IntlProvider } from "react-intl";

import "@learningblocks/shared-components/lib/i18n";
import plotFunctions from "./data/plot-calculations";
import { Scrolly } from "@learningblocks/interactives/scrollable-components";

interface SceneData {
  [key: string]: any;
}

const fetchSceneData = async (): Promise<SceneData> => {
  const sceneDataPath = `${window.CONTENT_BASE_URL}scene.json`;
  const response = await fetch(sceneDataPath);

  const contentType = response.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    const projectPath = window.location.pathname
      .split("/")
      .filter(Boolean)
      .pop();
    throw new Error(
      `Scene data not found. The scrollable project ${projectPath} might not exist. Please check the URL or contact support.`
    );
  }
  return response.json();
};

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

export class ProductionErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("ProductionErrorBoundary caught an error:", error, errorInfo);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="query-error">
          <div
            style={{
              color: "white",
              fontSize: "20px",
              padding: "20px",
              textAlign: "center",
            }}
          >
            <p
              style={{ color: "white", marginBottom: "15px", fontSize: "24px" }}
            >
              <strong>An error occurred.</strong>
            </p>
            <p style={{ color: "white", marginBottom: "15px" }}>
              Check console for details.
            </p>
            <p style={{ color: "white", marginBottom: "15px" }}>
              Error: {this.state.error?.message}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

interface ProductionScrollableProps {
  lang: string;
}

export default function ProductionScrollable({
  lang,
}: ProductionScrollableProps): React.ReactElement {
  useEffect(() => {
    i18next.changeLanguage(lang);
  }, [lang]);

  const compressedLang = (): string => {
    const req = new XMLHttpRequest();
    const animationLocation = `${window.CONTENT_BASE_URL}locales/${lang}/animation.lottie`;

    try {
      req.open("HEAD", animationLocation, false);
      req.send();
      return req.status === 200 ? lang : "en";
    } catch {
      return "en";
    }
  };

  const { data: sceneData } = useQuery<SceneData>(
    "scene-data",
    fetchSceneData,
    {
      suspense: true,
      retry: false,
    }
  );

  return (
    <IntlProvider locale={lang} messages={{}}>
      <Scrolly
        animationData=""
        sceneData={sceneData}
        plotFunctions={plotFunctions}
        animLng={compressedLang()}
      />
    </IntlProvider>
  );
}
